/**
 * セッション履歴ドロワーコンポーネント
 *
 * 過去の会話セッション一覧を表示
 */

import {
  Stack,
  Group,
  Text,
  Loader,
  Drawer,
  Badge,
  Divider,
  UnstyledButton,
} from "@mantine/core";
import { Plus } from "lucide-react";
import { SessionSummary } from "../../services/assistantService";
import { formatDate } from "../../utils/formatters";

interface SessionHistoryDrawerProps {
  opened: boolean;
  onClose: () => void;
  sessions: SessionSummary[];
  isLoading: boolean;
  currentSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onNewSession: () => void;
}

/**
 * セッション履歴ドロワー
 */
export function SessionHistoryDrawer({
  opened,
  onClose,
  sessions,
  isLoading,
  currentSessionId,
  onSelect,
  onNewSession,
}: SessionHistoryDrawerProps) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="会話履歴"
      position="right"
      size="sm"
      overlayProps={{ backgroundOpacity: 0.3, blur: 2 }}
    >
      <Stack gap="md">
        <UnstyledButton
          onClick={() => {
            onNewSession();
            onClose();
          }}
          style={{
            padding: "0.75rem",
            borderRadius: "8px",
            border: "1px dashed var(--mantine-color-dimmed)",
            textAlign: "center",
          }}
        >
          <Group gap="xs" justify="center">
            <Plus size={18} />
            <Text size="sm">新しい会話を開始</Text>
          </Group>
        </UnstyledButton>

        <Divider label="過去の会話" labelPosition="center" />

        {isLoading ? (
          <Stack align="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              読み込み中...
            </Text>
          </Stack>
        ) : sessions.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            まだ会話がありません
          </Text>
        ) : (
          <Stack gap="xs">
            {sessions.map((session) => (
              <UnstyledButton
                key={session.id}
                onClick={() => {
                  onSelect(session.id);
                  onClose();
                }}
                style={{
                  padding: "0.75rem",
                  borderRadius: "8px",
                  backgroundColor:
                    session.id === currentSessionId
                      ? "var(--mantine-color-blue-light)"
                      : "transparent",
                  border:
                    session.id === currentSessionId
                      ? "1px solid var(--mantine-color-blue-4)"
                      : "1px solid var(--mantine-color-dark-4)",
                }}
              >
                <Text size="sm" fw={500} lineClamp={1}>
                  {session.title}
                </Text>
                <Group gap="xs" mt={4}>
                  <Text size="xs" c="dimmed">
                    {formatDate(session.updatedAt)}
                  </Text>
                  <Badge size="xs" variant="light">
                    {session.messageCount}件
                  </Badge>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
}
